import { useRef, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { UploadCloud, Upload, RefreshCw, Info, AlertCircle } from "lucide-react"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { PageHeader } from "@/app/components/PageHeader"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import type { ClientTheme } from "@/app/lib/themes"
import { ErrorBanner, ReadOnlyCard, formatApiError } from "@/app/pages/UnitsPage/UnitsPage"
import { primaryButton } from "@/app/lib/buttonStyles"

interface ImportSummary {
  inserted: number
  updated: number
  unchanged: number
  skipped_app_touched: number
  errored: { tx_number: string; reason: string }[]
}

export function ImportPage() {
  const { theme } = useClientTheme()
  const { isStaff, isLoading: userLoading } = useCurrentUser()
  const queryClient = useQueryClient()

  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const invalidate = () => {
    for (const key of ["inventories", "transactions", "orri_inventories"]) {
      queryClient.invalidateQueries({ queryKey: [key] })
    }
  }

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<ImportSummary>("/imports", body),
    onSuccess: invalidate,
  })

  function importSheet() {
    mutation.reset()
    mutation.mutate({ source: "google_sheet" })
  }

  async function importFile(file: File | undefined | null) {
    if (!file) return
    mutation.reset()
    const text = await file.text()
    mutation.mutate({ source: "file", csv: text })
  }

  if (userLoading) {
    return (
      <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
        <div
          className="max-w-[900px]"
          style={{ color: theme.textMuted, fontFamily: "var(--font-ui)" }}
        >
          Loading…
        </div>
      </div>
    )
  }

  if (!isStaff) {
    return (
      <ReadOnlyCard
        theme={theme}
        title="Import"
        subtitle="Import transactions from the source sheet or a file"
        icon={<Upload size={17} strokeWidth={1.75} />}
      />
    )
  }

  const summary = mutation.data

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div className="max-w-[900px]">
        <PageHeader
          title="Import"
          subtitle="Import transactions from the source sheet or a file"
          theme={theme}
          icon={<Upload size={17} strokeWidth={1.75} />}
        />

        <div
          className="rounded-lg p-3 flex items-start gap-2 mb-6"
          style={{
            backgroundColor: `${theme.brandPrimary}0C`,
            border: `1px solid ${theme.brandPrimary}28`,
          }}
        >
          <Info size={15} style={{ color: theme.brandPrimary, marginTop: "1px", flexShrink: 0 }} />
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "12px",
              color: theme.textMuted,
              lineHeight: 1.45,
            }}
          >
            Re-importing is safe. Rows that have been edited in the app are skipped (reported as
            “skipped (app touched)”), so an import never clobbers local edits.
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
          {/* Google Sheet */}
          <div
            className="rounded-lg p-6 flex flex-col"
            style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
          >
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "10px",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: theme.textMuted,
                marginBottom: "6px",
              }}
            >
              From Google Sheet
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "12px",
                color: theme.textMuted,
                marginBottom: "16px",
                flex: 1,
              }}
            >
              Pull the latest transactions directly from the connected Google Sheet.
            </div>
            <button
              type="button"
              onClick={importSheet}
              disabled={mutation.isPending}
              className="btn"
              style={{
                ...primaryButton(theme),
                opacity: mutation.isPending ? 0.6 : 1,
                justifyContent: "center",
              }}
            >
              <RefreshCw size={14} />{" "}
              {mutation.isPending ? "Importing…" : "Import from Google Sheet"}
            </button>
          </div>

          {/* File */}
          <div
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragOver(false)
              importFile(e.dataTransfer.files?.[0])
            }}
            onClick={() => fileInputRef.current?.click()}
            className="rounded-lg p-6 flex flex-col items-center justify-center text-center"
            style={{
              backgroundColor: dragOver ? `${theme.brandPrimary}0C` : theme.bgSurface,
              border: `2px dashed ${dragOver ? theme.brandPrimary : theme.borderColor}`,
              cursor: "pointer",
              transition: "all 150ms ease",
            }}
          >
            <UploadCloud size={28} style={{ color: theme.brandPrimary, marginBottom: "8px" }} />
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "13px",
                fontWeight: 600,
                color: theme.textPrimary,
              }}
            >
              {mutation.isPending ? "Importing…" : "Import from File"}
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "12px",
                color: theme.textMuted,
                marginTop: "4px",
              }}
            >
              Drop a CSV here, or click to browse.
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                importFile(e.target.files?.[0])
                e.target.value = ""
              }}
            />
          </div>
        </div>

        {mutation.isError && <ErrorBanner theme={theme} message={formatApiError(mutation.error)} />}

        {summary && (
          <div
            className="rounded-lg p-6"
            style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
          >
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "10px",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: theme.textMuted,
                marginBottom: "16px",
              }}
            >
              Import Summary
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <SummaryStat
                label="Inserted"
                value={summary.inserted}
                color={theme.brandTeal}
                theme={theme}
              />
              <SummaryStat
                label="Updated"
                value={summary.updated}
                color={theme.brandPrimary}
                theme={theme}
              />
              <SummaryStat
                label="Unchanged"
                value={summary.unchanged}
                color={theme.textMuted}
                theme={theme}
              />
              <SummaryStat
                label="Skipped (app touched)"
                value={summary.skipped_app_touched}
                color={theme.brandGold}
                theme={theme}
              />
            </div>

            {summary.errored.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertCircle size={14} style={{ color: theme.brandAccent }} />
                  <span
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: theme.brandAccent,
                    }}
                  >
                    {summary.errored.length} row{summary.errored.length === 1 ? "" : "s"} errored
                  </span>
                </div>
                <div
                  className="rounded-lg overflow-hidden"
                  style={{ border: `1px solid ${theme.borderColor}` }}
                >
                  <table className="w-full border-collapse">
                    <thead>
                      <tr
                        style={{
                          backgroundColor: theme.bgSurface2,
                          borderBottom: `1px solid ${theme.borderColor}`,
                        }}
                      >
                        <th className="px-4 py-2 text-left" style={headStyle(theme)}>
                          TX #
                        </th>
                        <th className="px-4 py-2 text-left" style={headStyle(theme)}>
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.errored.map((row, i) => (
                        <tr
                          key={`${row.tx_number}-${i}`}
                          style={{ borderBottom: `1px solid ${theme.borderColor}` }}
                        >
                          <td
                            className="px-4 py-2"
                            style={{
                              fontFamily: "var(--font-data)",
                              fontSize: "12px",
                              color: theme.textPrimary,
                            }}
                          >
                            {row.tx_number || "—"}
                          </td>
                          <td
                            className="px-4 py-2"
                            style={{
                              fontFamily: "var(--font-ui)",
                              fontSize: "12px",
                              color: theme.textMuted,
                            }}
                          >
                            {row.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function SummaryStat({
  label,
  value,
  color,
  theme,
}: {
  label: string
  value: number
  color: string
  theme: ClientTheme
}) {
  return (
    <div
      className="rounded-lg p-4"
      style={{ backgroundColor: theme.bgSurface2, border: `1px solid ${theme.borderColor}` }}
    >
      <div style={{ fontFamily: "var(--font-data)", fontSize: "28px", fontWeight: 600, color }}>
        {value}
      </div>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "11px",
          fontWeight: 600,
          color: theme.textDim,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginTop: "2px",
        }}
      >
        {label}
      </div>
    </div>
  )
}

function headStyle(theme: ClientTheme): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "10px",
    fontWeight: 600,
    color: theme.textDim,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  }
}
